import { ReactNode, createContext, useContext, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { some } from 'lodash-es'
import {
  useBlockAction,
  useDeleteDocument,
  useDeleteFolder,
  useGuidToCrossSection,
  useGuidToElementIncludingRips,
  useProjectFiles,
} from '@editorHooks'
import { PROPERTIES_BLOCK_ACTION } from '../../constants'

interface DocumentTreeContextValue {
  guidToElement: Record<string, ShapeObject | ShapeObjectLine | Rip | Lintel>
  guidToCrossSection: Record<string, CrossSection>
  deleteDocument: (id: string) => void
  deletingDocumentId: string | null
  deleteFolder: (id: string) => void
  deletingFolderId: string | null
  // Deleting waits until the properties of the selected item are saved
  isDeleteBlocked: boolean
}

const DocumentTreeContext = createContext<DocumentTreeContextValue | null>(null)

/**
 * Provides the document tree's shared, project-wide data and mutations so each
 * tree row no longer has to instantiate them itself. Building the element /
 * cross-section maps and the delete mutations once (instead
 * of once per row) is what keeps the tree fast for large projects.
 */
export const DocumentTreeProvider = ({
  rootGuid,
  selectedDocument,
  disableActions,
  children,
}: {
  rootGuid: string
  selectedDocument: string | null
  disableActions: boolean
  children: ReactNode
}) => {
  const { data } = useProjectFiles(rootGuid)

  const guidToElement = useGuidToElementIncludingRips()
  const guidToCrossSection = useGuidToCrossSection()

  const {
    mutate: deleteDocument,
    isLoading: isDeletingDocument,
    variables: deletingDocumentVariables,
  } = useDeleteDocument(rootGuid)

  const {
    mutate: deleteFolder,
    isLoading: isDeletingFolder,
    variables: deletingFolderVariables,
  } = useDeleteFolder(rootGuid)

  const { isBlocked: isDeleteBlocked } = useBlockAction(PROPERTIES_BLOCK_ACTION, 'subscriber')

  // A single delete listener for the selected item, instead of one per row.
  useHotkeys(
    'delete',
    () => {
      if (!selectedDocument || disableActions || isDeleteBlocked) return

      if (some(data.sections, { guid: selectedDocument })) {
        deleteFolder(selectedDocument)
      } else {
        deleteDocument(selectedDocument)
      }
    },
    { enabled: !!selectedDocument && !disableActions && !isDeleteBlocked },
    [
      selectedDocument,
      disableActions,
      isDeleteBlocked,
      data.sections,
      deleteFolder,
      deleteDocument,
    ],
  )

  const value = useMemo<DocumentTreeContextValue>(
    () => ({
      guidToElement,
      guidToCrossSection,
      deleteDocument,
      deletingDocumentId: isDeletingDocument ? deletingDocumentVariables ?? null : null,
      deleteFolder,
      deletingFolderId: isDeletingFolder ? deletingFolderVariables ?? null : null,
      isDeleteBlocked,
    }),
    [
      guidToElement,
      guidToCrossSection,
      deleteDocument,
      isDeletingDocument,
      deletingDocumentVariables,
      deleteFolder,
      isDeletingFolder,
      deletingFolderVariables,
      isDeleteBlocked,
    ],
  )

  return <DocumentTreeContext.Provider value={value}>{children}</DocumentTreeContext.Provider>
}

export const useDocumentTreeContext = (): DocumentTreeContextValue => {
  const context = useContext(DocumentTreeContext)
  if (!context) {
    throw new Error('useDocumentTreeContext must be used within a DocumentTreeProvider')
  }
  return context
}
